import { AromaComboboxVariantProps } from "@zenchef/styled-system/recipes";
import { Combobox, type ListCollection } from "@ark-ui/react";
import { Portal } from "@ark-ui/react";
import { Text } from "../typography/Text";

import { useState } from "react";
import Icon from "../Icon/Icon";

import {
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxInput,
  ComboboxIndicator,
  ComboboxContent,
  ComboboxItem,
  ComboboxItemText,
  ComboboxItemIndicator,
} from "./comboboxParts";
import { useFilterOptions } from "./filterOptions";

type CollectionItem = ListCollection<{
  label: string;
  value: string;
  isHighlighted: boolean;
}>["items"][number];

export interface ComboboxProps extends AromaComboboxVariantProps {
  collection: ListCollection;
  multiselect?: boolean;
  value?: string[];
  defaultValue?: string[];
  placeholder?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  className?: string;
  contentClassName?: string;
  closeOnSelect?: boolean;
  onChange: (value: string[] | string) => void;
  prefix?: string;
  suffix?: string;
  minSearchLength?: number;
  maxDropdownItems?: number;
  filterOnSearch?: boolean;
}

export const ComboboxDropdown: React.FC<ComboboxProps> = ({
  collection,
  multiselect,
  defaultValue,
  placeholder,
  disabled,
  defaultOpen,
  onChange,
  className,
  contentClassName,
  closeOnSelect = true,
  prefix,
  suffix,
  value,
  minSearchLength = 0,
  maxDropdownItems,
  filterOnSearch = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>(value?.[0] ?? "");
  const [selectedValues, setSelectedValues] = useState<string[]>(
    defaultValue ?? [],
  );

  const filteredOptions = useFilterOptions({
    filterOnSearch,
    query,
    minSearchLength,
    maxDropdownItems,
    collection,
  });

  const handleChange = (value: string) => {
    if (multiselect) {
      const values = selectedValues.includes(value)
        ? selectedValues.filter((val) => val !== value)
        : [...selectedValues, value];
      onChange(values);
      setSelectedValues(values);
    } else {
      onChange([value]);
      setSelectedValues([value]);
    }
    closeOnSelect && setIsOpen(false);
  };

  return (
    <ComboboxRoot
      className={className}
      closeOnSelect={closeOnSelect}
      collection={collection}
      open={defaultOpen ?? isOpen}
      onOpenChange={() => setIsOpen(!isOpen)}
      value={selectedValues}
      defaultValue={defaultValue}
      onValueChange={({ value }: { value: string[] }) => handleChange(value[0])}
      inputValue={query}
      onInputValueChange={({ inputValue }) => setQuery(inputValue)}
      {...props}
    >
      <Combobox.Control>
        <ComboboxTrigger>
          {prefix ? <Text textStyle="title.xs">{prefix}</Text> : null}
          <ComboboxInput
            // onChange={(e) => setQuery(e.target.value)}
            onClick={() => setIsOpen(true)}
            // value={value}
            placeholder={placeholder}
            disabled={disabled}
          />
          {suffix ? <Text textStyle="title.xs">{suffix}</Text> : null}
          <ComboboxIndicator>
            <Icon
              name={isOpen ? "chevron-up" : "chevron-down"}
              fontSize="125"
            />
          </ComboboxIndicator>
        </ComboboxTrigger>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner>
          <ComboboxContent className={contentClassName}>
            <Combobox.ItemGroup>
              {filteredOptions?.map((item: CollectionItem) => (
                <ComboboxItem
                  key={item.value}
                  item={item}
                  data-highlighted={item.isHighlighted ? "true" : "false"}
                >
                  <ComboboxItemText>{item.label}</ComboboxItemText>
                  <ComboboxItemIndicator>
                    <Icon name="check-01" />
                  </ComboboxItemIndicator>
                </ComboboxItem>
              ))}
            </Combobox.ItemGroup>
          </ComboboxContent>
        </Combobox.Positioner>
      </Portal>
    </ComboboxRoot>
  );
};

export default ComboboxDropdown;
